import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Import pour accéder à Redux
import Modal from 'fast-modal-library';
import HidePasswordIcon from '../components/HidePasswordIcon';

const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*+.~/-]).{8,}$/;



const apiPrefix = process.env.REACT_APP_API_PREFIX;

export const shouldRedirectAfterHide = (isSuccess) => Boolean(isSuccess);
export const isSuccessResponse = (response) => Boolean(response && response.ok);


const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();
    const { token } = useParams();

    // Récupération de l'appNumber depuis Redux
    const appNumber = useSelector((state) => state.app.appNumber);
    
    useEffect(() => {
        const checkToken = async () => {
            try {
                const response = await fetch(apiPrefix + `reset/${token}?appNumber=${appNumber}`, {
                    method: 'GET',
                });
    
                if (response.status !== 200) {
                    console.error('Erreur lors de la validation du token:', await response.text());
                    navigate('/error');
                }
            } catch (error) {
                console.error('Erreur réseau lors de la validation du token:', error);
                navigate('/error');
            }
        };
    
        checkToken();
    }, [token, navigate, appNumber]);
    

    const hide = () => {
        setShowModal(false);
        /* istanbul ignore next */
        if (shouldRedirectAfterHide(isSuccess)) {
            navigate('/signin');
        }
    };

    const resetPassword = async (e) => {
        e.preventDefault();

        if (!appNumber) {
            console.error('AppNumber manquant dans Redux');
            setMessage('Erreur interne : appNumber non défini');
            setShowModal(true);
            return;
        }
    
        if (password !== confirmPassword) {
            setMessage('Les mots de passe ne correspondent pas');
            setShowModal(true);
            return;
        }
    
        if (!regexPassword.test(password)) {
            setMessage('Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.');
            setShowModal(true);
            return;
        }
    
        try {
            const response = await fetch(apiPrefix + `reset/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ appNumber, password }),
            });
            
    
            const data = await response.json();
    
            /* istanbul ignore next */
            if (isSuccessResponse(response)) {
                setIsSuccess(true);
            }
    
            setMessage(data.message);
            setShowModal(true);
        } catch (error) {
            console.error('Erreur lors de la réinitialisation du mot de passe :', error);
            setMessage('Une erreur est survenue lors de la réinitialisation du mot de passe.');
            setShowModal(true);
        }
    };
    

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="card-theme">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-theme-primary mb-6">
                            <i className="fas fa-key mr-3 text-theme-accent"></i>
                            Réinitialiser le mot de passe
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Veuillez saisir votre nouveau mot de passe ci-dessous.
                        </p>
                    </div>
                    
                    <form onSubmit={resetPassword} className="space-y-6">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-lock mr-2 text-theme-accent"></i>
                                Nouveau mot de passe
                            </label>
                            <div className="relative">
                                <input 
                                    type={showPassword ? 'text' : 'password'} 
                                    id="password"
                                    placeholder="Saisissez votre nouveau mot de passe" 
                                    value={password} 
                                    onChange={e => setPassword(e.target.value)} 
                                    className="input-theme pr-12"
                                    required 
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <HidePasswordIcon showPassword={showPassword} setShowPassword={setShowPassword} />
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-lock mr-2 text-theme-accent"></i>
                                Confirmer le nouveau mot de passe
                            </label>
                            <input 
                                type={showPassword ? 'text' : 'password'} 
                                id="confirmPassword"
                                placeholder="Confirmez votre nouveau mot de passe" 
                                value={confirmPassword} 
                                onChange={e => setConfirmPassword(e.target.value)} 
                                className="input-theme"
                                required 
                            />
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-blue-800 mb-2">
                                <i className="fas fa-info-circle mr-2"></i>
                                Critères du mot de passe
                            </h3>
                            <ul className="text-xs text-blue-700 space-y-1">
                                <li>• Au moins 8 caractères</li>
                                <li>• Une majuscule (A-Z)</li>
                                <li>• Une minuscule (a-z)</li>
                                <li>• Un chiffre (0-9)</li>
                                <li>• Un caractère spécial (#?!@$%^&*+.~/-)</li>
                            </ul>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="btn-theme w-full"
                        >
                            <i className="fas fa-save mr-2"></i>
                            Réinitialiser le mot de passe
                        </button>
                    </form>
                </div>
                
                {showModal && (
                    <Modal contentModal={message} hide={hide} />
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
